import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { v4 as uuidv4 } from 'uuid';

const ChatComponent = ({ selectedPersonName, onAnalyzePerson }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const CHAT_ENDPOINT = process.env.REACT_APP_CHAT_WEBHOOK_URL || 'https://ediand.com.br/webhook-test/ediChatAnalises';

  // Gerar um novo sessionId ao montar o componente ou quando a pessoa selecionada mudar
  useEffect(() => {
    setSessionId(uuidv4());
    setMessages([]); // Limpar o chat ao trocar de pessoa
  }, [selectedPersonName]);

  // Rolagem automática para o final do chat
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async (messageText) => {
    if (!messageText.trim()) return;

    const newMessage = { sender: 'user', text: messageText };
    setMessages((prevMessages) => [...prevMessages, newMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      if (!sessionId) {
        throw new Error("Session ID not generated.");
      }

      const payload = {
        sessionId: sessionId,
        action: "sendMessage",
        chatInput: messageText,
      };

      const response = await fetch(CHAT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': '536918df-1fcf-46bf-82eb-f7ddae4cd9e2', // Adicionado o header de autorização
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Erro de rede: ${response.status}`);
      }

      const data = await response.json();

      if (data && data.output) {
        const botMessage = { sender: 'bot', text: data.output };
        setMessages((prevMessages) => [...prevMessages, botMessage]);
      } else {
        throw new Error("Resposta do LLM vazia ou em formato inesperado.");
      }

    } catch (err) {
      console.error("Erro no chat:", err);
      setMessages((prevMessages) => [...prevMessages, { sender: 'bot', text: `\`\`\`Erro\`\`\`\n\nDesculpe, houve um erro ao processar sua solicitação: ${err.message}. Por favor, tente novamente.` }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !isLoading) {
      sendMessage(inputMessage);
    }
  };

  const handleAnalyzeButtonClick = () => {
    const analysisPrompt = `Efetuar uma análise da pessoa: ${selectedPersonName}`;
    sendMessage(analysisPrompt);
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg flex flex-col h-[500px]">
      <h3 className="text-xl font-bold text-gray-800 mb-4">EdiChat</h3>

      {selectedPersonName && (
        <button
          onClick={handleAnalyzeButtonClick}
          className="mb-4 p-3 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors duration-200"
          disabled={!selectedPersonName || isLoading}
        >
          Efetuar uma análise da pessoa: {selectedPersonName}
        </button>
      )}

      <div className="flex-1 overflow-y-auto border border-gray-200 rounded-md p-3 mb-4 space-y-3">
        {messages.length === 0 && !isLoading && (
          <div className="text-center text-gray-500 italic mt-10">
            {selectedPersonName ? `Olá! Converse sobre ${selectedPersonName} aqui.` : 'Selecione uma pessoa para iniciar o chat.'}
          </div>
        )}
        {messages.map((msg, index) => (
          <div
            key={index}
            className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[70%] p-3 rounded-lg ${
                msg.sender === 'user'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 text-gray-800'
              }`}
            >
              <ReactMarkdown>{msg.text}</ReactMarkdown>
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="flex justify-start">
            <div className="max-w-[70%] p-3 rounded-lg bg-gray-200 text-gray-800">
              <div className="animate-pulse flex space-x-2">
                <div className="h-2 w-2 bg-gray-500 rounded-full"></div>
                <div className="h-2 w-2 bg-gray-500 rounded-full"></div>
                <div className="h-2 w-2 bg-gray-500 rounded-full"></div>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="flex">
        <input
          type="text"
          className="flex-1 p-3 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder={selectedPersonName ? `Pergunte sobre ${selectedPersonName}...` : 'Selecione uma pessoa antes de digitar...'}
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={!selectedPersonName || isLoading}
        />
        <button
          onClick={() => sendMessage(inputMessage)}
          className="p-3 bg-blue-600 text-white rounded-r-md hover:bg-blue-700 transition-colors duration-200"
          disabled={!selectedPersonName || isLoading || !inputMessage.trim()}
        >
          Enviar
        </button>
      </div>
    </div>
  );
};

export default ChatComponent;